import { Box, Stack, Typography } from "@mui/material";
import { GeneratorStatusExceedanceDirection } from "generated-graphql/graphql";

type Props = {
  direction?: GeneratorStatusExceedanceDirection | null;
  /** Detected mismatching months folded into the run; null until the next sweep. */
  mismatchMonths?: number | null;
  /** Trailing consecutive months the mismatch has persisted. */
  sustainedMonths?: number | null;
  detectedAt?: string | null;
  validatedAt?: string | null;
  /** How the row was resolved, already labelled; null while it is still open. */
  resolutionMethod?: string | null;
  /** Read-only: assignment lives on the linked notification submission. */
  staffAssigneeName?: string | null;
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Whole days between an ISO timestamp and now (>= 0). */
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** Compact label-over-value pair for the metadata strip. */
function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

/**
 * Lifecycle metadata under the drawer's status chips, as labeled fields: kind of
 * discrepancy, how long its run is, when it was detected/validated, how it was
 * resolved, and who owns it. Gives the TPM/RS context without leaving the drawer.
 */
export function ExceedanceMetaLine({
  direction,
  mismatchMonths,
  sustainedMonths,
  detectedAt,
  validatedAt,
  resolutionMethod,
  staffAssigneeName,
}: Props) {
  return (
    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
      <MetaField
        label="Type"
        value={
          direction === GeneratorStatusExceedanceDirection.Downward
            ? "Downward trend"
            : "Exceedance"
        }
      />
      {mismatchMonths != null && <MetaField label="Months detected" value={`${mismatchMonths}`} />}
      {sustainedMonths != null && (
        <MetaField label="Consecutive months" value={`${sustainedMonths}`} />
      )}
      {detectedAt && (
        <MetaField
          label="First detected"
          value={`${formatDate(detectedAt)} · ${daysSince(detectedAt)}d open`}
        />
      )}
      {validatedAt && <MetaField label="Validated" value={formatDate(validatedAt)} />}
      {resolutionMethod && <MetaField label="Resolved by" value={resolutionMethod} />}
      {staffAssigneeName && <MetaField label="Owner" value={staffAssigneeName} />}
    </Stack>
  );
}
